// Player actions and their key bindings: two slots (primary / secondary) per
// action, rebindable in the settings. Pure functions, no DOM.

export type Action =
  | 'forward'
  | 'back'
  | 'left'
  | 'right'
  | 'jump'
  | 'crouch'
  | 'prone'
  | 'sprint'
  | 'activate'
  | 'save'
  | 'load'
  | 'respawn'
  | 'noclip'
  | 'replay'
  | 'chat'
  | 'help'
  | 'debug'
  | 'scoreboard'
  | 'voteYes'
  | 'voteNo'
  | 'spectate'
  | 'followNext'
  | 'followPrev'
  | 'thirdPerson'
  | 'save2'
  | 'load2'
  | 'save3'
  | 'load3'
  | 'tjlRecord';

/** A bound key: KeyboardEvent.code, "Mouse1".."Mouse5" or "WheelUp"/"WheelDown"; null = empty slot. */
export type BindSlot = string | null;
export type Binds = Record<Action, [BindSlot, BindSlot]>;

export const ACTIONS: { action: Action; label: string; group: 'Movement' | 'Run' | 'Camera' | 'Interface' }[] = [
  { action: 'forward', label: 'Move forward', group: 'Movement' },
  { action: 'back', label: 'Move back', group: 'Movement' },
  { action: 'left', label: 'Strafe left', group: 'Movement' },
  { action: 'right', label: 'Strafe right', group: 'Movement' },
  { action: 'jump', label: 'Jump', group: 'Movement' },
  { action: 'crouch', label: 'Crouch', group: 'Movement' },
  { action: 'prone', label: 'Prone', group: 'Movement' },
  { action: 'sprint', label: 'Sprint', group: 'Movement' },
  { action: 'activate', label: 'Use (buttons, doors)', group: 'Movement' },
  { action: 'save', label: 'Save position', group: 'Run' },
  { action: 'load', label: 'Load position', group: 'Run' },
  { action: 'respawn', label: 'Respawn / restart run', group: 'Run' },
  { action: 'noclip', label: 'Noclip', group: 'Run' },
  { action: 'replay', label: 'Watch last run', group: 'Run' },
  { action: 'save2', label: 'Save position (slot 2)', group: 'Run' },
  { action: 'load2', label: 'Load position (slot 2)', group: 'Run' },
  { action: 'save3', label: 'Save position (slot 3)', group: 'Run' },
  { action: 'load3', label: 'Load position (slot 3)', group: 'Run' },
  { action: 'tjlRecord', label: 'Record trickjump line', group: 'Run' },
  { action: 'thirdPerson', label: 'Third person view', group: 'Camera' },
  { action: 'spectate', label: 'Spectate / play', group: 'Camera' },
  { action: 'followNext', label: 'Spectate next player', group: 'Camera' },
  { action: 'followPrev', label: 'Spectate previous player', group: 'Camera' },
  { action: 'chat', label: 'Chat', group: 'Interface' },
  { action: 'scoreboard', label: 'Scoreboard (hold)', group: 'Interface' },
  { action: 'voteYes', label: 'Vote yes', group: 'Interface' },
  { action: 'voteNo', label: 'Vote no', group: 'Interface' },
  { action: 'help', label: 'Toggle help', group: 'Interface' },
  { action: 'debug', label: 'Toggle debug info', group: 'Interface' },
];

export const DEFAULT_BINDS: Binds = {
  forward: ['KeyW', 'ArrowUp'],
  back: ['KeyS', 'ArrowDown'],
  left: ['KeyA', 'ArrowLeft'],
  right: ['KeyD', 'ArrowRight'],
  jump: ['Space', 'Mouse2'],
  crouch: ['KeyC', 'ControlLeft'],
  prone: ['KeyX', null],
  sprint: ['ShiftLeft', null],
  activate: ['KeyF', null],
  save: ['KeyQ', 'Mouse4'],
  load: ['KeyE', 'Mouse5'],
  respawn: ['KeyR', null],
  noclip: ['KeyN', null],
  replay: ['KeyG', null],
  chat: ['KeyT', null],
  help: ['KeyH', null],
  debug: ['F3', null],
  scoreboard: ['Tab', null],
  voteYes: ['F1', null],
  voteNo: ['F2', null],
  spectate: ['KeyY', null],
  followNext: ['Mouse1', 'BracketRight'],
  followPrev: ['BracketLeft', null],
  thirdPerson: ['KeyV', null],
  save2: [null, null],
  load2: [null, null],
  save3: [null, null],
  load3: [null, null],
  tjlRecord: ['KeyK', null],
};

/** Keys that can't be bound (Escape always opens the menu / cancels). */
export const RESERVED_KEYS = new Set(['Escape']);

export function cloneBinds(b: Binds): Binds {
  const out = {} as Binds;
  for (const a of Object.keys(b) as Action[]) out[a] = [b[a][0], b[a][1]];
  return out;
}

/** Merges stored binds over the defaults: unknown actions are dropped, missing ones get their defaults. */
export function normalizeBinds(stored: unknown): Binds {
  const out = cloneBinds(DEFAULT_BINDS);
  if (!stored || typeof stored !== 'object') return out;
  const used = new Set<string>();
  const missing: Action[] = [];
  for (const a of Object.keys(out) as Action[]) {
    const v = (stored as Record<string, unknown>)[a];
    if (!Array.isArray(v)) {
      missing.push(a);
      continue;
    }
    const slot = (x: unknown): BindSlot => (typeof x === 'string' && x && !RESERVED_KEYS.has(x) ? x : null);
    out[a] = [slot(v[0]), slot(v[1])];
    for (const code of out[a]) if (code) used.add(code);
  }
  // F was the replay key before +activate existed: binds from then get F for activate and G for the replay
  if (missing.includes('activate') && out.replay[0] === 'KeyF' && !used.has('KeyG')) {
    out.replay[0] = 'KeyG';
    used.delete('KeyF');
    used.add('KeyG');
  }
  // actions added since the binds were saved: their default keys only where still free
  for (const a of missing) out[a] = [out[a][0] && !used.has(out[a][0]) ? out[a][0] : null, out[a][1] && !used.has(out[a][1]) ? out[a][1] : null];
  return out;
}

/** Version of the default binds; stored binds of an older version are migrated once (migrateBinds). */
export const BINDS_VERSION = 2;

/**
 * Migrates binds saved by an older version. 2: F was the replay key before
 * +activate existed, so binds saved then have activate unbound (or missing):
 * activate gets F, the replay G (if free).
 */
export function migrateBinds(binds: Binds, from: number): Binds {
  const out = cloneBinds(binds);
  const activateFree = (!out.activate[0] && !out.activate[1]) || out.activate.includes('KeyF');
  if (from < 2 && activateFree && out.replay.includes('KeyF')) {
    const gUsed = Object.values(out).some((slots) => slots.includes('KeyG'));
    out.replay = out.replay.map((k) => (k === 'KeyF' ? (gUsed ? null : 'KeyG') : k)) as [BindSlot, BindSlot];
    if (!out.activate.includes('KeyF')) out.activate = ['KeyF', out.activate[1]];
  }
  return out;
}

/** Keys bound to more than one action (code -> actions). */
export function findConflicts(binds: Binds): Map<string, Action[]> {
  const byCode = new Map<string, Action[]>();
  for (const a of Object.keys(binds) as Action[]) {
    for (const code of binds[a]) {
      if (!code) continue;
      const list = byCode.get(code) ?? [];
      if (!list.includes(a)) list.push(a);
      byCode.set(code, list);
    }
  }
  for (const [code, list] of byCode) if (list.length < 2) byCode.delete(code);
  return byCode;
}

export interface AssignResult {
  binds: Binds;
  /** The action (and its slot) that had the key before; it now holds the key this slot had (a swap). */
  displaced: { action: Action; slot: 0 | 1 } | null;
}

/**
 * Binds `code` to `action`'s `slot`. If another slot already has the key, the
 * two slots swap keys so nothing is bound twice. `code` null clears the slot.
 */
export function assign(binds: Binds, action: Action, slot: 0 | 1, code: BindSlot): AssignResult {
  const out = cloneBinds(binds);
  const previous = out[action][slot];
  let displaced: AssignResult['displaced'] = null;
  if (code) {
    for (const a of Object.keys(out) as Action[]) {
      for (const s of [0, 1] as const) {
        if ((a === action && s === slot) || out[a][s] !== code) continue;
        out[a][s] = previous;
        if (a !== action) displaced = { action: a, slot: s };
      }
    }
  }
  out[action][slot] = code;
  return { binds: out, displaced };
}

/** Human readable key name. */
export function keyLabel(code: BindSlot): string {
  if (!code) return '—';
  const m = /^Mouse(\d)$/.exec(code);
  if (m) return ['Mouse 1', 'Mouse 2', 'Mouse 3', 'Mouse 4', 'Mouse 5'][Number(m[1]) - 1] ?? code;
  const named: Record<string, string> = {
    WheelUp: 'Wheel Up',
    WheelDown: 'Wheel Down',
    Space: 'Space',
    ShiftLeft: 'L-Shift',
    ShiftRight: 'R-Shift',
    ControlLeft: 'L-Ctrl',
    ControlRight: 'R-Ctrl',
    AltLeft: 'L-Alt',
    AltRight: 'R-Alt',
    MetaLeft: 'L-Meta',
    MetaRight: 'R-Meta',
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
    Backquote: '`',
    Minus: '-',
    Equal: '=',
    BracketLeft: '[',
    BracketRight: ']',
    Backslash: '\\',
    Semicolon: ';',
    Quote: "'",
    Comma: ',',
    Period: '.',
    Slash: '/',
    CapsLock: 'Caps',
    Enter: 'Enter',
    Tab: 'Tab',
  };
  if (named[code]) return named[code];
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Numpad')) return `Num ${code.slice(6)}`;
  return code;
}

/** "SPACE / MOUSE 2" style label of an action's keys (for hints). */
export function bindLabel(binds: Binds, action: Action): string {
  const keys = binds[action].filter((c): c is string => !!c).map(keyLabel);
  return keys.length ? keys.join(' / ') : 'unbound';
}
